import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GoMakeDialog } from "@/components/dialog/go-make-dialog";
import { Button, IconButton, Stack, TextField, Tooltip } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useStyle } from "./style";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "he", label: "עברית" },
  { code: "ar", label: "العربية" },
  { code: "de", label: "Deutsch" },
  { code: "tr", label: "Türkçe" },
  { code: "ja", label: "日本語" },
  { code: "pl", label: "Polski" },
  { code: "sq", label: "Shqip" },
  { code: "ru", label: "Русский" },
  { code: "es", label: "Español" },
  { code: "pt", label: "Português" },
  { code: "it", label: "Italiano" },
  { code: "fr", label: "Français" },
  { code: "zh", label: "中文" },
];

interface IProductTranslation {
  name: string;
  lang: string;
}

interface IProductTranslationModalProps {
  open: boolean;
  onClose: () => void;
  translations: IProductTranslation[];
  onSave: (translations: IProductTranslation[]) => void;
  productName?: string;
  title?: string;
}

const ProductTranslationModal: React.FC<IProductTranslationModalProps> = ({
  open,
  onClose,
  translations,
  onSave,
  productName = "",
  title,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyle();
  const [extraLanguages, setExtraLanguages] = useState<string[]>([]);
  const [hiddenLanguages, setHiddenLanguages] = useState<string[]>([]);
  const [localTranslations, setLocalTranslations] = useState<Record<string, string>>({});
  const [languageNames, setLanguageNames] = useState<Record<string, string>>({});
  const [newLanguage, setNewLanguage] = useState("");
  const [newTranslation, setNewTranslation] = useState("");

  const languageRows = useMemo(() => {
    const codes = [
      ...LANGUAGES.map((language) => language.code),
      ...extraLanguages,
    ];
    return Array.from(new Set(codes.filter(Boolean))).filter(
      (code) => !hiddenLanguages.some((hidden) => hidden.toLowerCase() === code.toLowerCase())
    );
  }, [extraLanguages, hiddenLanguages]);

  useEffect(() => {
    if (!open) return;
    const initialTranslations: Record<string, string> = {};
    const extras: string[] = [];
    (translations || []).forEach((item) => {
      if (!item?.lang) return;
      initialTranslations[item.lang] = item.name || "";
      if (!LANGUAGES.some((language) => language.code === item.lang)) {
        extras.push(item.lang);
      }
    });
    if (productName && !initialTranslations.en) {
      initialTranslations.en = productName;
    }
    const initialNames: Record<string, string> = {};
    [...LANGUAGES.map((language) => language.code), ...extras].forEach((code) => {
      const known = LANGUAGES.find((language) => language.code === code);
      initialNames[code] = known?.label || code;
    });
    setLanguageNames(initialNames);
    setLocalTranslations(initialTranslations);
    setExtraLanguages(Array.from(new Set(extras)));
    setHiddenLanguages([]);
    setNewLanguage("");
    setNewTranslation("");
  }, [open, translations, productName]);

  const handleTranslationChange = (lang: string, value: string) => {
    setLocalTranslations((prev) => ({
      ...prev,
      [lang]: value,
    }));
  };

  const addLanguage = () => {
    const code = newLanguage.trim();
    const name = newTranslation.trim();
    if (!code || !name) return;
    const existing = languageRows.find((row) => row.toLowerCase() === code.toLowerCase());
    const lang = existing || code;
    if (!existing) {
      setExtraLanguages((current) => [...current, code]);
    }
    setHiddenLanguages((current) => current.filter((item) => item.toLowerCase() !== lang.toLowerCase()));
    setLocalTranslations((current) => ({ ...current, [lang]: name }));
    setNewLanguage("");
    setNewTranslation("");
  };

  const removeLanguage = (lang: string) => {
    setHiddenLanguages((current) => [...current, lang]);
    setExtraLanguages((current) => current.filter((item) => item.toLowerCase() !== lang.toLowerCase()));
    setLocalTranslations((current) => {
      const next = { ...current };
      delete next[lang];
      return next;
    });
  };

  const resolveLang = (code: string) => {
    const edited = (languageNames[code] ?? "").trim();
    if (!edited || edited === languageLabel(code) || edited === code) return code;
    const known = LANGUAGES.find(
      (language) => language.label.toLowerCase() === edited.toLowerCase() || language.code.toLowerCase() === edited.toLowerCase()
    );
    return known?.code || edited;
  };

  const handleSave = () => {
    const translationsArray: IProductTranslation[] = languageRows
      .map((lang) => ({
        name: localTranslations[lang] || "",
        lang: resolveLang(lang),
      }))
      .filter((item) => item.name.trim().length > 0);

    onSave(translationsArray);
    onClose();
  };

  const languageLabel = (code: string) =>
    LANGUAGES.find((language) => language.code === code)?.label || code;

  return (
    <GoMakeDialog
      open={open}
      onClose={onClose}
      title={title || t("products.addProduct.admin.translateProductName")}
      onPrimaryAction={handleSave}
      primaryButtonLabel={t("common.save")}
      cancelButtonLabel={t("common.cancel")}
      maxWidth="sm"
    >
      <Stack spacing={2} sx={classes.container}>
        {languageRows.map((lang) => (
          <Stack key={lang} direction="row" alignItems="center" spacing={1}>
            <TextField
              size="small"
              fullWidth
              placeholder={t("products.addProduct.admin.language")}
              value={languageNames[lang] ?? languageLabel(lang)}
              onChange={(e) => setLanguageNames((current) => ({ ...current, [lang]: e.target.value }))}
            />
            <TextField
              size="small"
              fullWidth
              placeholder={t("products.addProduct.admin.translationName")}
              value={localTranslations[lang] || ""}
              onChange={(e) => handleTranslationChange(lang, e.target.value)}
            />
            <Tooltip title={t("navigationButtons.delete")}>
              <IconButton
                size="small"
                aria-label={t("navigationButtons.delete")}
                onClick={() => removeLanguage(lang)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ))}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ borderTop: "1px solid #E6E8F0", pt: 1.5 }}>
          <TextField
            size="small"
            fullWidth
            placeholder={t("products.addProduct.admin.language")}
            value={newLanguage}
            onChange={(e) => setNewLanguage(e.target.value)}
          />
          <TextField
            size="small"
            fullWidth
            placeholder={t("products.addProduct.admin.translationName")}
            value={newTranslation}
            onChange={(e) => setNewTranslation(e.target.value)}
          />
          <Button
            variant="contained"
            size="small"
            disabled={!newLanguage.trim() || !newTranslation.trim()}
            onClick={addLanguage}
            sx={{ flexShrink: 0, height: 40, minWidth: 72, px: 1.5, textTransform: "none" }}
          >
            {t("materials.buttons.add")}
          </Button>
        </Stack>
      </Stack>
    </GoMakeDialog>
  );
};

export { ProductTranslationModal };
export type { IProductTranslation };

